import React, { useState, useEffect } from "react";
import "./AuditLogs.css";
import axios from "axios";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Pagination from "@mui/material/Pagination";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";
import Loader from "../../Components/Loader/Loader";
import { useSelector } from "react-redux";
import { fetchAuditLogs } from "../../Components/Utils/index";
import dayjs from "dayjs";

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    paddingRight: "30px",
    borderColor: "#ddd",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#EF5443",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#EF5443",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#666",
  },
  "& .MuiSelect-icon": {
    color: "#f44336",
  },
}));

const CustomDatePicker = styled(DatePicker)({
  "& .MuiInputBase-root": {
    width: "250px",
    height: "50px",
    paddingRight: "14px !important",
  },
  "& .MuiInputBase-input": {
    color: "#ef5443",
    fontSize: "15px",
  },
  "& .MuiInputAdornment-root": {
    marginRight: "0",
  },
  "& input::-webkit-inner-spin-button": {
    "-webkit-appearance": "none",
    margin: "0",
  },
  "& input::-webkit-outer-spin-button": {
    "-webkit-appearance": "none",
    margin: "0",
  },
  "& input[type=number]": {
    "-moz-appearance": "textfield",
  },
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#334555",
    color: theme.palette.common.white,
    fontWeight: 500,
  },
}));

const PlaceholderTypography = styled("div")(({ theme }) => ({
  color: "#888",
  fontSize: "16px",
}));

const AuditLogs = () => {
  const tableOptions = [
    { value: "ap_admin_audit", label: "Admin Audit" },
    { value: "ap_agent_audit", label: "Agent Audit" },
    { value: "ap_markup_audit", label: "Markup Audit" },
    { value: "client_details_audit", label: "Client Details Audit" },
    { value: "currency_audit", label: "Currency Audit" },
    { value: "transaction_details_audit", label: "Transaction Details Audit" },
  ];

  const [tableName, setTableName] = useState("transaction_details_audit");
  const [action, setAction] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [clientId, setClientId] = useState("");
  const [airlineCode, setAirlineCode] = useState("");
  const [tableData, setTableData] = useState(null);
  const [totalResults, setTotalResults] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [showLoader, setShowLoader] = useState(false);
  const [agentList, setAgentList] = useState(null);
  const [airLinesList, setAirlinesList] = useState(null);
  const loggedInUserDetails = useSelector(
    (state) => state.loggedInUserDetails.loggedInUserDetails
  );

  const actionOptions = [
    { value: "INSERT", label: "INSERT" },
    { value: "UPDATE", label: "UPDATE" },
    { value: "DELETE", label: "DELETE" },
  ];

  useEffect(() => {
    fetchAirlineList();
    if (loggedInUserDetails?.role === "admin") {
      fetchClientListForLoggedInAdmin();
    }
  }, [loggedInUserDetails]);

  useEffect(() => {
    if (page > 0 && tableName) {
      handleFetchAuditLogs();
    }
  }, [page]);

  const fetchAirlineList = () => {
    const headers = {
      "Content-Type": "application/json",
      Authorization: localStorage.getItem("AuthToken"),
    };
    axios
      .get("https://api.aeroprime.in/crm-service/admin/getAirlineList", {
        headers,
      })
      .then((response) => setAirlinesList(response.data))
      .catch((error) => {
        if (error.response?.status === 401) {
          localStorage.clear();
          window.location.href = "/";
        }
      });
  };

  const fetchClientListForLoggedInAdmin = () => {
    const headers = {
      "Content-Type": "application/json",
      Authorization: localStorage.getItem("AuthToken"),
    };
    axios
      .get("https://api.aeroprime.in/crm-service/client/details", {
        headers,
      })
      .then((response) => setAgentList(response.data))
      .catch((error) => {
        if (error.response?.status === 401) {
          localStorage.clear();
          window.location.href = "/";
        }
      });
  };

  const handleFetchAuditLogs = () => {
    if (!tableName) {
      alert("Please select a table");
      return;
    }

    setShowLoader(true);

    const params = {
      tableName: tableName,
      page: page,
      size: size,
    };

    if (fromDate) {
      params.from = dayjs(fromDate).format("YYYY-MM-DD");
    }

    if (toDate) {
      params.to = dayjs(toDate).format("YYYY-MM-DD");
    }

    if (action) {
      params.action = action;
    }

    if (clientId) {
      params.clientId = clientId;
    } else if (loggedInUserDetails?.role === "agent") {
      params.clientId = loggedInUserDetails?.clientId;
    }

    if (airlineCode && airlineCode !== "" && airlineCode !== "All") {
      params.airlineCode = airlineCode;
    }

    fetchAuditLogs(params)
      .then((response) => {
        console.log("Audit Logs API Response:", response);
        if (response?.data) {
          // Handle different response structures
          let data = [];
          let total = 0;
          
          if (Array.isArray(response.data)) {
            // If response.data is directly an array
            data = response.data;
            total = response.data.length;
          } else if (response.data.content && Array.isArray(response.data.content)) {
            // Spring Boot pagination structure
            data = response.data.content;
            total = response.data.totalElements || response.data.total || 0;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            // Custom pagination structure
            data = response.data.data;
            total = response.data.totalCount || response.data.total || 0;
          } else if (response.data.auditLogs && Array.isArray(response.data.auditLogs)) {
            // Alternative structure
            data = response.data.auditLogs;
            total = response.data.total || 0;
          }
          
          console.log("Processed Audit Logs Data:", data);
          console.log("Total Results:", total);
          
          setTableData(data);
          setTotalResults(total);
        } else {
          console.warn("No data in response:", response);
          setTableData([]);
          setTotalResults(0);
        }
        setShowLoader(false);
      })
      .catch((error) => {
        setShowLoader(false);
        console.error("Error fetching audit logs:", error);
        if (error.response?.status === 401) {
          localStorage.clear();
          window.location.href = "/";
        } else {
          alert("Error fetching audit logs. Please try again.");
        }
      });
  };

  const handlePageChange = (event, value) => {
    setPage(value - 1);
  };

  const totalPages = Math.ceil(totalResults / size);

  // Format timestamp to readable date
  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    try {
      if (typeof timestamp === 'number') {
        return new Date(timestamp).toLocaleString();
      } else if (typeof timestamp === 'string') {
        return new Date(timestamp).toLocaleString();
      }
      return timestamp;
    } catch (e) {
      return timestamp;
    }
  };

  // Format boolean/INT values
  const formatBoolean = (value) => {
    if (value === null || value === undefined) return "-";
    if (value === 1 || value === true) return "Yes";
    if (value === 0 || value === false) return "No";
    return value;
  };

  // Get table headers based on table type
  const getTableHeaders = () => {
    switch (tableName) {
      case "transaction_details_audit":
        return (
          <>
            <StyledTableCell>Audit ID</StyledTableCell>
            <StyledTableCell>ID</StyledTableCell>
            <StyledTableCell>Action</StyledTableCell>
            <StyledTableCell>Reference ID</StyledTableCell>
            <StyledTableCell>Booking ID</StyledTableCell>
            <StyledTableCell>Client ID</StyledTableCell>
            <StyledTableCell>Amount</StyledTableCell>
            <StyledTableCell>Balance</StyledTableCell>
            <StyledTableCell>Airline Code</StyledTableCell>
            <StyledTableCell>Remarks</StyledTableCell>
            <StyledTableCell>Transaction Date</StyledTableCell>
            <StyledTableCell>Changed At</StyledTableCell>
            <StyledTableCell>Updated By</StyledTableCell>
          </>
        );
      case "ap_admin_audit":
        return (
          <>
            <StyledTableCell>Audit ID</StyledTableCell>
            <StyledTableCell>ID</StyledTableCell>
            <StyledTableCell>Action</StyledTableCell>
            <StyledTableCell>User Name</StyledTableCell>
            <StyledTableCell>Email</StyledTableCell>
            <StyledTableCell>Phone</StyledTableCell>
            <StyledTableCell>Is Super Admin</StyledTableCell>
            <StyledTableCell>Is Active</StyledTableCell>
            <StyledTableCell>Airline Codes</StyledTableCell>
            <StyledTableCell>Airline Currency Code</StyledTableCell>
            <StyledTableCell>Updated By</StyledTableCell>
            <StyledTableCell>Updated At</StyledTableCell>
            <StyledTableCell>Changed At</StyledTableCell>
          </>
        );
      case "ap_agent_audit":
        return (
          <>
            <StyledTableCell>Audit ID</StyledTableCell>
            <StyledTableCell>ID</StyledTableCell>
            <StyledTableCell>Action</StyledTableCell>
            <StyledTableCell>User Name</StyledTableCell>
            <StyledTableCell>Email</StyledTableCell>
            <StyledTableCell>Phone</StyledTableCell>
            <StyledTableCell>Client ID</StyledTableCell>
            <StyledTableCell>Is Super Agent</StyledTableCell>
            <StyledTableCell>Is Master Agent</StyledTableCell>
            <StyledTableCell>Is Active</StyledTableCell>
            <StyledTableCell>Updated By</StyledTableCell>
            <StyledTableCell>Updated At</StyledTableCell>
            <StyledTableCell>Changed At</StyledTableCell>
          </>
        );
      case "ap_markup_audit":
        return (
          <>
            <StyledTableCell>Audit ID</StyledTableCell>
            <StyledTableCell>ID</StyledTableCell>
            <StyledTableCell>Action</StyledTableCell>
            <StyledTableCell>Client ID</StyledTableCell>
            <StyledTableCell>Airline Code</StyledTableCell>
            <StyledTableCell>Markup Value</StyledTableCell>
            <StyledTableCell>Updated By</StyledTableCell>
            <StyledTableCell>Updated At</StyledTableCell>
            <StyledTableCell>Changed At</StyledTableCell>
          </>
        );
      case "client_details_audit":
        return (
          <>
            <StyledTableCell>Audit ID</StyledTableCell>
            <StyledTableCell>Action</StyledTableCell>
            <StyledTableCell>Client ID</StyledTableCell>
            <StyledTableCell>Client Name</StyledTableCell>
            <StyledTableCell>Client Email</StyledTableCell>
            <StyledTableCell>Balance</StyledTableCell>
            <StyledTableCell>Credit Limit</StyledTableCell>
            <StyledTableCell>Currency Code</StyledTableCell>
            <StyledTableCell>Currency Symbol</StyledTableCell>
            <StyledTableCell>Airline Codes</StyledTableCell>
            <StyledTableCell>Airline Currency Code</StyledTableCell>
            <StyledTableCell>Is Active</StyledTableCell>
            <StyledTableCell>Request Interval (min)</StyledTableCell>
            <StyledTableCell>Request Limit</StyledTableCell>
            <StyledTableCell>Updated By</StyledTableCell>
            <StyledTableCell>Updated At</StyledTableCell>
            <StyledTableCell>Changed At</StyledTableCell>
          </>
        );
      case "currency_audit":
        return (
          <>
            <StyledTableCell>Audit ID</StyledTableCell>
            <StyledTableCell>ID</StyledTableCell>
            <StyledTableCell>Action</StyledTableCell>
            <StyledTableCell>From Currency</StyledTableCell>
            <StyledTableCell>To Currency</StyledTableCell>
            <StyledTableCell>Conversion Rate</StyledTableCell>
            <StyledTableCell>Updated By</StyledTableCell>
            <StyledTableCell>Updated At</StyledTableCell>
            <StyledTableCell>Changed At</StyledTableCell>
          </>
        );
      default:
        return (
          <>
            <StyledTableCell style={{ minWidth: "80px" }}>ID</StyledTableCell>
            <StyledTableCell style={{ minWidth: "100px" }}>Action</StyledTableCell>
            <StyledTableCell style={{ minWidth: "150px" }}>Table Name</StyledTableCell>
            <StyledTableCell style={{ minWidth: "120px" }}>Changed By</StyledTableCell>
            <StyledTableCell style={{ minWidth: "150px" }}>Changed At</StyledTableCell>
            <StyledTableCell style={{ minWidth: "200px", maxWidth: "300px" }}>Old Values</StyledTableCell>
            <StyledTableCell style={{ minWidth: "200px", maxWidth: "300px" }}>New Values</StyledTableCell>
          </>
        );
    }
  };

  // Get column count for colspan
  const getColumnCount = () => {
    switch (tableName) {
      case "transaction_details_audit": return 13;
      case "ap_admin_audit": return 13;
      case "ap_agent_audit": return 13;
      case "ap_markup_audit": return 9;
      case "client_details_audit": return 17;
      case "currency_audit": return 9;
      default: return 7;
    }
  };

  // Render table row based on table type
  const renderTableRow = (row, index) => {
    switch (tableName) {
      case "transaction_details_audit":
        return (
          <>
            <TableCell>{row.audit_id || "-"}</TableCell>
            <TableCell>{row.id || "-"}</TableCell>
            <TableCell>{row.action_type || "-"}</TableCell>
            <TableCell>{row.reference_id || "-"}</TableCell>
            <TableCell>{row.booking_id || "-"}</TableCell>
            <TableCell>{row.client_id || "-"}</TableCell>
            <TableCell>{row.amount !== null && row.amount !== undefined ? row.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}</TableCell>
            <TableCell>{row.balance !== null && row.balance !== undefined ? row.balance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}</TableCell>
            <TableCell>{row.airline_code || "-"}</TableCell>
            <TableCell style={{ wordBreak: "break-word", maxWidth: "200px" }}>{row.remarks || "-"}</TableCell>
            <TableCell>{formatDate(row.transaction_date)}</TableCell>
            <TableCell>{formatDate(row.changed_at || row.updated_at)}</TableCell>
            <TableCell>{row.updated_by || "-"}</TableCell>
          </>
        );
      case "ap_admin_audit":
        return (
          <>
            <TableCell>{row.audit_id || "-"}</TableCell>
            <TableCell>{row.id || "-"}</TableCell>
            <TableCell>{row.action_type || "-"}</TableCell>
            <TableCell>{row.user_name || "-"}</TableCell>
            <TableCell>{row.email || "-"}</TableCell>
            <TableCell>{row.phone || "-"}</TableCell>
            <TableCell>{formatBoolean(row.is_super_admin)}</TableCell>
            <TableCell>{formatBoolean(row.is_active)}</TableCell>
            <TableCell style={{ wordBreak: "break-word", maxWidth: "200px" }}>{row.airline_codes || "-"}</TableCell>
            <TableCell>{row.airline_currency_code || "-"}</TableCell>
            <TableCell>{row.updated_by || "-"}</TableCell>
            <TableCell>{formatDate(row.updated_at)}</TableCell>
            <TableCell>{formatDate(row.changed_at)}</TableCell>
          </>
        );
      case "ap_agent_audit":
        return (
          <>
            <TableCell>{row.audit_id || "-"}</TableCell>
            <TableCell>{row.id || "-"}</TableCell>
            <TableCell>{row.action_type || "-"}</TableCell>
            <TableCell>{row.user_name || "-"}</TableCell>
            <TableCell>{row.email || "-"}</TableCell>
            <TableCell>{row.phone || "-"}</TableCell>
            <TableCell>{row.client_id || "-"}</TableCell>
            <TableCell>{formatBoolean(row.is_super_agent)}</TableCell>
            <TableCell>{formatBoolean(row.is_master_agent)}</TableCell>
            <TableCell>{formatBoolean(row.is_active)}</TableCell>
            <TableCell>{row.updated_by || "-"}</TableCell>
            <TableCell>{formatDate(row.updated_at)}</TableCell>
            <TableCell>{formatDate(row.changed_at)}</TableCell>
          </>
        );
      case "ap_markup_audit":
        return (
          <>
            <TableCell>{row.audit_id || "-"}</TableCell>
            <TableCell>{row.id || "-"}</TableCell>
            <TableCell>{row.action_type || "-"}</TableCell>
            <TableCell>{row.client_id || "-"}</TableCell>
            <TableCell>{row.airline_code || "-"}</TableCell>
            <TableCell>{row.markup_value !== null && row.markup_value !== undefined ? parseFloat(row.markup_value).toFixed(2) : "-"}</TableCell>
            <TableCell>{row.updated_by || "-"}</TableCell>
            <TableCell>{formatDate(row.updated_at)}</TableCell>
            <TableCell>{formatDate(row.changed_at)}</TableCell>
          </>
        );
      case "client_details_audit":
        return (
          <>
            <TableCell>{row.audit_id || "-"}</TableCell>
            <TableCell>{row.action_type || "-"}</TableCell>
            <TableCell>{row.client_id || "-"}</TableCell>
            <TableCell>{row.client_name || "-"}</TableCell>
            <TableCell>{row.client_email || "-"}</TableCell>
            <TableCell>{row.balance !== null && row.balance !== undefined ? row.balance.toLocaleString('en-IN') : "-"}</TableCell>
            <TableCell>{row.credit_limit !== null && row.credit_limit !== undefined ? row.credit_limit.toLocaleString('en-IN') : "-"}</TableCell>
            <TableCell>{row.currency_code || "-"}</TableCell>
            <TableCell>{row.currency_symbol || "-"}</TableCell>
            <TableCell style={{ wordBreak: "break-word", maxWidth: "200px" }}>{row.airline_codes || "-"}</TableCell>
            <TableCell>{row.airline_currency_code || "-"}</TableCell>
            <TableCell>{formatBoolean(row.is_active)}</TableCell>
            <TableCell>{row.request_interval_minutes || "-"}</TableCell>
            <TableCell>{row.request_limit || "-"}</TableCell>
            <TableCell>{row.updated_by || "-"}</TableCell>
            <TableCell>{formatDate(row.updated_at)}</TableCell>
            <TableCell>{formatDate(row.changed_at)}</TableCell>
          </>
        );
      case "currency_audit":
        return (
          <>
            <TableCell>{row.audit_id || "-"}</TableCell>
            <TableCell>{row.id || "-"}</TableCell>
            <TableCell>{row.action_type || "-"}</TableCell>
            <TableCell>{row.from_currency || "-"}</TableCell>
            <TableCell>{row.to_currency || "-"}</TableCell>
            <TableCell>{row.conversion_rate !== null && row.conversion_rate !== undefined ? parseFloat(row.conversion_rate).toFixed(4) : "-"}</TableCell>
            <TableCell>{row.updated_by || "-"}</TableCell>
            <TableCell>{formatDate(row.updated_at)}</TableCell>
            <TableCell>{formatDate(row.changed_at)}</TableCell>
          </>
        );
      default:
        // Generic fallback for unknown tables
        const rowId = row.id || row.auditId || row.audit_id || index + 1;
        const rowAction = row.action || row.actionType || row.action_type || "-";
        const rowTableName = row.tableName || row.table_name || tableName || "-";
        const rowChangedBy = row.changedBy || row.userId || row.user_id || row.changed_by || row.modifiedBy || row.modified_by || row.createdBy || row.created_by || row.updated_by || "-";
        const rowChangedAt = row.changedAt || row.timestamp || row.createdAt || row.created_at || row.changed_at || row.modifiedAt || row.modified_at || row.createdDate || row.created_date || row.updated_at || "-";
        const oldValues = row.oldValues || row.old_values || row.oldData || row.old_data || row.previousValues || row.previous_values || null;
        const newValues = row.newValues || row.new_values || row.newData || row.new_data || row.currentValues || row.current_values || null;
        
        return (
          <>
            <TableCell>{rowId}</TableCell>
            <TableCell>{rowAction}</TableCell>
            <TableCell>{rowTableName}</TableCell>
            <TableCell>{rowChangedBy}</TableCell>
            <TableCell>{rowChangedAt}</TableCell>
            <TableCell style={{ wordBreak: "break-word", maxWidth: "300px" }}>
              {oldValues
                ? typeof oldValues === "string"
                  ? oldValues.substring(0, 100) + (oldValues.length > 100 ? "..." : "")
                  : JSON.stringify(oldValues).substring(0, 100) + (JSON.stringify(oldValues).length > 100 ? "..." : "")
                : "-"}
            </TableCell>
            <TableCell style={{ wordBreak: "break-word", maxWidth: "300px" }}>
              {newValues
                ? typeof newValues === "string"
                  ? newValues.substring(0, 100) + (newValues.length > 100 ? "..." : "")
                  : JSON.stringify(newValues).substring(0, 100) + (JSON.stringify(newValues).length > 100 ? "..." : "")
                : "-"}
            </TableCell>
          </>
        );
    }
  };

  return (
    <div className="audit-logs-wrapper">
      <div className="Audit-heading">AUDIT LOGS</div>
      <div className="Break-line-rp"></div>
      <div className="audit-selection-section">
        {showLoader && <Loader hideLoader={false} />}
        <div className="audit-table-section">
          <StyledFormControl style={{ width: "250px" }}>
            <Select
              id="table-select"
              value={tableName}
              onChange={(event) => {
                setTableName(event.target.value);
                setTableData(null);
                setPage(0);
              }}
              displayEmpty
              renderValue={(selected) => {
                if (!selected || selected === "") {
                  return <PlaceholderTypography>Table</PlaceholderTypography>;
                }
                return tableOptions.find((opt) => opt.value === selected)?.label || selected;
              }}
              style={{
                color: "#000",
                fontSize: "15px",
                width: "250px",
                height: "55px",
              }}
              IconComponent={(props) => (
                <ExpandMoreIcon
                  {...props}
                  style={{
                    color: "#ff5722",
                    marginRight: "8px",
                  }}
                />
              )}
              MenuProps={{
                PaperProps: {
                  style: {
                    border: "1px solid #E5E2DA",
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                    marginTop: "8px",
                    borderRadius: "6px",
                    maxHeight: "200px",
                    overflow: "auto",
                  },
                },
                MenuListProps: {
                  style: {
                    padding: 0,
                    maxHeight: "200px",
                    overflowY: "auto",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                  },
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                },
              }}
            >
              {tableOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </StyledFormControl>
        </div>

        <div className="audit-action-section">
          <StyledFormControl style={{ width: "250px" }}>
            <Select
              id="action-select"
              value={action}
              onChange={(event) => {
                setAction(event.target.value);
                setTableData(null);
                setPage(0);
              }}
              displayEmpty
              renderValue={(selected) => {
                if (!selected || selected === "") {
                  return <PlaceholderTypography>Action</PlaceholderTypography>;
                }
                return selected;
              }}
              style={{
                color: "#000",
                fontSize: "15px",
                width: "250px",
                height: "55px",
              }}
              IconComponent={(props) => (
                <ExpandMoreIcon
                  {...props}
                  style={{
                    color: "#ff5722",
                    marginRight: "8px",
                  }}
                />
              )}
              MenuProps={{
                PaperProps: {
                  style: {
                    border: "1px solid #E5E2DA",
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                    marginTop: "8px",
                    borderRadius: "6px",
                    maxHeight: "200px",
                    overflow: "auto",
                  },
                },
                MenuListProps: {
                  style: {
                    padding: 0,
                    maxHeight: "200px",
                    overflowY: "auto",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                  },
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                },
              }}
            >
              <MenuItem value="">All Actions</MenuItem>
              {actionOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </StyledFormControl>
        </div>

        {loggedInUserDetails?.role === "admin" && (
          <div className="audit-client-selection-section">
            <StyledFormControl variant="outlined" style={{ width: "250px" }}>
              <Select
                id="client-select"
                value={clientId}
                onChange={(event) => {
                  setClientId(event.target.value);
                  setTableData(null);
                  setPage(0);
                }}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected || selected === "") {
                    return <PlaceholderTypography>Client</PlaceholderTypography>;
                  }
                  const selectedItem = agentList?.[selected];
                  return selectedItem || selected;
                }}
                style={{
                  color: "#000",
                  fontSize: "15px",
                  width: "250px",
                  height: "55px",
                }}
                IconComponent={(props) => (
                  <ExpandMoreIcon
                    {...props}
                    style={{
                      color: "#ff5722",
                      marginRight: "8px",
                    }}
                  />
                )}
                MenuProps={{
                  PaperProps: {
                    style: {
                      border: "1px solid #E5E2DA",
                      boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                      marginTop: "8px",
                      borderRadius: "6px",
                      maxHeight: "200px",
                      maxWidth: "300px",
                      overflow: "auto",
                    },
                  },
                  MenuListProps: {
                    style: {
                      padding: 0,
                      maxHeight: "200px",
                      maxWidth: "300px",
                      overflowY: "auto",
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                    },
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                  },
                }}
              >
                <MenuItem value="">All Clients</MenuItem>
                {agentList &&
                  Object.keys(agentList)
                    .filter((key) => agentList[key] !== "All Agents")
                    .map((key) => (
                      <MenuItem key={key} value={key}>
                        {agentList[key]}
                      </MenuItem>
                    ))}
              </Select>
            </StyledFormControl>
          </div>
        )}
        
        <div className="audit-airline-section">
          <StyledFormControl style={{ width: "250px" }}>
            <Select
              id="airline-select"
              value={airlineCode}
              onChange={(event) => {
                setAirlineCode(event.target.value);
                setTableData(null);
                setPage(0);
              }}
              displayEmpty
              renderValue={(selected) => {
                if (!selected || selected === "" || selected === "All") {
                  return <PlaceholderTypography>Airline</PlaceholderTypography>;
                }
                return selected;
              }}
              style={{
                color: "#000",
                fontSize: "15px",
                width: "250px",
                height: "55px",
              }}
              IconComponent={(props) => (
                <ExpandMoreIcon
                  {...props}
                  style={{
                    color: "#ff5722",
                    marginRight: "8px",
                  }}
                />
              )}
              MenuProps={{
                PaperProps: {
                  style: {
                    border: "1px solid #E5E2DA",
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                    marginTop: "8px",
                    borderRadius: "6px",
                    maxHeight: "200px",
                    overflow: "auto",
                  },
                },
                MenuListProps: {
                  style: {
                    padding: 0,
                    maxHeight: "200px",
                    overflowY: "auto",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                  },
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                },
              }}
            >
              <MenuItem value="">All Airlines</MenuItem>
              {airLinesList?.map((airline) => (
                <MenuItem key={airline} value={airline}>
                  {airline}
                </MenuItem>
              ))}
            </Select>
          </StyledFormControl>
        </div>
      </div>

      <div className="audit-dates-section">
        <div className="start-date-picker-section">
          <StyledFormControl fullWidth>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DatePicker"]} sx={{ height: '60px' }}>
                <CustomDatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={(newValue) => {
                    setFromDate(newValue);
                    setTableData(null);
                    setPage(0);
                  }}
                />
              </DemoContainer>
            </LocalizationProvider>
          </StyledFormControl>
        </div>
        <div className="end-date-picker-section">
          <StyledFormControl fullWidth>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DatePicker"]} sx={{ height: '60px' }}>
                <CustomDatePicker
                  label="To Date"
                  value={toDate}
                  onChange={(newValue) => {
                    setToDate(newValue);
                    setTableData(null);
                    setPage(0);
                  }}
                />
              </DemoContainer>
            </LocalizationProvider>
          </StyledFormControl>
        </div>
      </div>

      <div className="audit-fetch-btn-wrapper">
        <button className="fetch-btn" onClick={handleFetchAuditLogs}>
          Fetch Audit Logs
        </button>
      </div>

      {tableData !== null && (
        <>
          <div className="Break-line-rp"></div>
          <TableContainer component={Paper} style={{ marginTop: "20px", maxWidth: "100%", overflowX: "auto" }}>
            <Table sx={{ minWidth: 650 }} aria-label="audit logs table">
              <TableHead>
                <TableRow>
                  {getTableHeaders()}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData && tableData.length > 0 ? (
                  tableData.map((row, index) => (
                    <TableRow key={row.audit_id || row.id || index} hover>
                      {renderTableRow(row, index)}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={getColumnCount()} align="center" style={{ padding: "40px" }}>
                      No Records Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <div style={{ marginTop: "20px", display: "flex", justifyContent: "center" }}>
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={handlePageChange}
                color="primary"
              />
            </div>
          )}

          {tableData && tableData.length > 0 && (
            <div style={{ marginTop: "10px", textAlign: "center", color: "#666" }}>
              Total Records: {totalResults}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AuditLogs;
